import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "@/lib/api";
import { fetchAdminTickets, patchTicketStatus } from "@/support/supportApi";

const TABS = [
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In Progress" },
  { id: "resolved", label: "Resolved" },
];

function formatWhen(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export default function AdminSupportSection() {
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState("open");
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  async function load() {
    const rows = await fetchAdminTickets();
    setTickets(rows);
  }

  useEffect(() => {
    load().catch((err) => setError(apiErrorMessage(err)));
  }, []);

  const visible = useMemo(
    () => tickets.filter((t) => t.status === tab),
    [tickets, tab],
  );
  const selected = tickets.find((t) => t.id === openId) || null;

  async function changeStatus(ticket, status) {
    if (!status || status === ticket.status) return;
    setBusyId(ticket.id);
    setError("");
    try {
      await patchTicketStatus(ticket.id, status);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="mp-admin-support">
      <div className="mp-top" style={{ marginTop: 28 }}>
        <div>
          <p className="mp-kicker">Support</p>
          <h2>Tickets</h2>
        </div>
      </div>

      <div className="mp-support-tabs">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? "is-on" : ""}
            onClick={() => {
              setTab(item.id);
              setOpenId(null);
            }}
          >
            {item.label}
            <span>
              {tickets.filter((t) => t.status === item.id).length}
            </span>
          </button>
        ))}
      </div>

      {error ? <p className="mp-error">{error}</p> : null}

      <div className="mp-table-wrap">
        <table className="mp-table">
          <thead>
            <tr>
              <th>Ticket ID</th>
              <th>User</th>
              <th>Email</th>
              <th>Category</th>
              <th>Subject</th>
              <th>Created</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((t) => (
              <tr key={t.id}>
                <td>
                  <button type="button" className="mp-linkish" onClick={() => setOpenId(t.id)}>
                    {t.id}
                  </button>
                </td>
                <td>{t.user_name || "—"}</td>
                <td>{t.user_email}</td>
                <td>{t.category}</td>
                <td>{t.subject}</td>
                <td>{formatWhen(t.created_at)}</td>
                <td>
                  <span className={`mp-pill ${t.status === "resolved" ? "on" : "off"}`}>{t.status}</span>
                </td>
                <td>
                  <select
                    className="mp-ticket-status"
                    disabled={busyId === t.id}
                    value={t.status}
                    onChange={(e) => changeStatus(t, e.target.value)}
                  >
                    <option value="open">open</option>
                    <option value="in_progress">in_progress</option>
                    <option value="resolved">resolved</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 ? <p className="mp-empty">No tickets in this status.</p> : null}
      </div>

      {selected ? (
        <article className="mp-ticket-detail">
          <header>
            <h3>Ticket #{selected.id}</h3>
            <Button size="sm" variant="outline" onClick={() => setOpenId(null)}>
              Close
            </Button>
          </header>
          <dl className="mpd-dl">
            <div><dt>User</dt><dd>{selected.user_name || "—"}</dd></div>
            <div><dt>Email</dt><dd>{selected.user_email}</dd></div>
            <div><dt>Category</dt><dd>{selected.category}</dd></div>
            <div><dt>Created</dt><dd>{formatWhen(selected.created_at)}</dd></div>
            <div><dt>Updated</dt><dd>{formatWhen(selected.updated_at)}</dd></div>
            <div><dt>Status</dt><dd>{selected.status}</dd></div>
            <div><dt>License</dt><dd>{selected.account?.license_status || "—"}</dd></div>
            <div><dt>Access</dt><dd>{selected.account?.has_access ? "On" : "Off"}</dd></div>
            <div><dt>Trial</dt><dd>{selected.account?.trial_status || "—"}</dd></div>
            <div><dt>Trial ends</dt><dd>{formatWhen(selected.account?.trial_ends_at)}</dd></div>
            <div><dt>Plan</dt><dd>{selected.account?.paid_plan || "—"}</dd></div>
            <div><dt>Billing</dt><dd>{selected.account?.billing_provider || "—"}</dd></div>
          </dl>
          <h4>Message</h4>
          <p className="mp-ticket-body">{selected.message}</p>
          <h4>Conversation</h4>
          <ul className="mp-ticket-thread">
            {(selected.conversation || []).map((turn, i) => (
              <li key={`${turn.role}-${i}`}>
                <strong>{turn.role}</strong>
                <p>{turn.content}</p>
              </li>
            ))}
          </ul>
          {(selected.conversation || []).length === 0 ? <p className="mp-empty">No chat transcript.</p> : null}
        </article>
      ) : null}
    </section>
  );
}
